import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
} from "@angular/core";

import { SelfServiceCommandKeys } from "../../../helpers/enums/selfServiceCommands";
import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { StartLoadMessage } from "../../../helpers/self-service/models/types";
import { ModifyL2VPNRemovePort } from "../../../helpers/self-service/modify_l2vpn";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { L2vpnSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { SelfserviceToggle } from "../selfservice-toggle/selfservice-toggle";
import { SelfserviceWizard } from "../selfservice-wizard/selfservice-wizard";

type MappedESI = NonNullable<L2vpnSubscriptionDetails>["esis"][number] & { selected: boolean };

@Component({
  selector: "selfservice-remove-port",
  templateUrl: "selfservice-remove-port.html",
  styleUrl: "../selfservice-add-port/selfservice-add-port.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, GuidPipe, SelfserviceToggle, SelfserviceWizard, TranslatePipe],
})
export class SelfserviceRemovePort {
  private auth = inject(AuthService);

  protected subscription = input.required<L2vpnSubscriptionDetails>();
  protected bus = input.required<EventEmitter<StartLoadMessage>>();

  protected mappedPorts = linkedSignal(
    () => this.subscription().esis.map((esi) => ({ ...esi, selected: true })) as MappedESI[],
  );

  protected summaryItemOneExpanded = signal(true);
  protected summaryItemTwoExpanded = signal(false);

  protected removedPorts = computed(() => this.mappedPorts().filter((port) => !port.selected));
  protected existingPorts = computed(() => this.mappedPorts().filter((port) => port.selected));
  protected disableSubmitButton = computed(() => !this.removedPorts().length || this.existingPorts().length < 2);

  protected toggleSummaryItemOne() {
    this.summaryItemOneExpanded.update((value) => !value);
  }

  protected toggleSummaryItemTwo() {
    this.summaryItemTwoExpanded.update((value) => !value);
  }

  protected readonly buildCommand = () => {
    const action = SelfServiceCommandKeys.ModifyL2VpnRemovePort;
    const modify = new ModifyL2VPNRemovePort();
    modify.subscription = this.subscription();
    modify.setEsis(
      this.removedPorts().map((esi) =>
        (esi.endpoints ?? []).map((endpoint) => ({
          subscription_id: endpoint.port!.subscriptionId,
          vlan: endpoint.vlanrange,
        })),
      ),
    );
    const command = new SelfServiceCommand(this.subscription().subscriptionId, action);
    command.payload = modify;
    return { command, action };
  };

  protected toggleESI(index: number) {
    this.mappedPorts.update((ports) => ports.map((p, i) => (i === index ? { ...p, selected: !p.selected } : p)));
  }

  protected isMyPort(port: NonNullable<MappedESI["endpoints"]>[number]["port"]) {
    return port?.customer?.customerId === this.auth.viewingCustomer().customerId;
  }
}
